import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import type { UploadAssessment } from '@internal/plugin-cloudflare-workers-common';
import {
  AppActivityResponse,
  WorkersListResponse,
  WorkerDetailResponse,
  WorkerLogsResponse,
  WorkerMetricsRange,
  WorkerMetricsResponse,
  RefreshResponse,
  UploadProjectResponse,
  CloudflareWorkersApi,
  VersionConfig,
  WorkerRepoConfig,
  PendingUpdate,
  PublishStatus,
  SourceDownload,
  SpotlightDigestResponse,
} from './types';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface CloudflareWorkersClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

/**
 * Best-effort extraction of the backend's `{ error: string }` envelope so the
 * server-supplied message (which may already include upstream context) reaches
 * the UI verbatim. Falls back to the HTTP statusText when no body is present.
 */
async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body?.error === 'string' && body.error.length > 0) {
      return body.error;
    }
  } catch {
    // Body wasn't JSON — fall through.
  }
  return response.statusText || `HTTP ${response.status}`;
}

async function throwApiError(
  response: Response,
  fallbackPrefix: string,
): Promise<never> {
  const detail = await readErrorMessage(response);
  throw new ApiError(`${fallbackPrefix}: ${detail}`, response.status);
}

export class CloudflareWorkersClient implements CloudflareWorkersApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: CloudflareWorkersClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getWorkers(): Promise<WorkersListResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(`${baseUrl}/workers`);

    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch workers');
    }

    return await response.json();
  }

  async getAppActivity(): Promise<AppActivityResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(`${baseUrl}/activity`);

    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch app activity');
    }

    return await response.json();
  }

  async getWorkerDetail(name: string): Promise<WorkerDetailResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(name)}`,
    );

    if (!response.ok) {
      await throwApiError(
        response,
        `Failed to fetch worker detail for "${name}"`,
      );
    }

    return await response.json();
  }

  async getWorkerMetrics(
    name: string,
    range: WorkerMetricsRange,
  ): Promise<WorkerMetricsResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const params = new URLSearchParams({ range });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(
        name,
      )}/metrics?${params.toString()}`,
    );

    if (!response.ok) {
      await throwApiError(
        response,
        `Failed to fetch worker metrics for "${name}"`,
      );
    }

    return await response.json();
  }

  async getWorkerLogs(
    name: string,
    range: WorkerMetricsRange,
    options: { errorsOnly?: boolean; cursor?: string } = {},
  ): Promise<WorkerLogsResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const params = new URLSearchParams({ range });
    if (options.errorsOnly) params.set('errorsOnly', 'true');
    if (options.cursor) params.set('cursor', options.cursor);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(
        name,
      )}/logs?${params.toString()}`,
    );

    if (!response.ok) {
      await throwApiError(
        response,
        `Failed to fetch worker logs for "${name}"`,
      );
    }

    return await response.json();
  }

  async getWorkerVersionConfig(
    name: string,
    versionId: string,
  ): Promise<VersionConfig> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(
        name,
      )}/versions/${encodeURIComponent(versionId)}/config`,
    );

    if (!response.ok) {
      await throwApiError(
        response,
        `Failed to fetch version config for "${name}" (${versionId})`,
      );
    }

    return await response.json();
  }

  async getWorkerRepoConfig(name: string): Promise<WorkerRepoConfig> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(name)}/repo-config`,
    );

    if (!response.ok) {
      await throwApiError(
        response,
        `Failed to fetch repo config for "${name}"`,
      );
    }

    return await response.json();
  }

  async refresh(): Promise<RefreshResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(`${baseUrl}/refresh`, {
      method: 'POST',
    });

    if (!response.ok && response.status !== 429) {
      await throwApiError(response, 'Failed to refresh workers');
    }

    return await response.json();
  }

  async putVariable(
    workerName: string,
    varName: string,
    value: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(
        workerName,
      )}/variables/${encodeURIComponent(varName)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      },
    );

    if (!response.ok) {
      await throwApiError(response, `Failed to save variable "${varName}"`);
    }
  }

  async deleteVariable(workerName: string, varName: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(
        workerName,
      )}/variables/${encodeURIComponent(varName)}`,
      { method: 'DELETE' },
    );

    if (!response.ok) {
      await throwApiError(response, `Failed to delete variable "${varName}"`);
    }
  }

  async putSecret(
    workerName: string,
    secretName: string,
    value: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(
        workerName,
      )}/secrets/${encodeURIComponent(secretName)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      },
    );

    if (!response.ok) {
      await throwApiError(response, `Failed to save secret "${secretName}"`);
    }
  }

  async deleteSecret(workerName: string, secretName: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workers/${encodeURIComponent(
        workerName,
      )}/secrets/${encodeURIComponent(secretName)}`,
      { method: 'DELETE' },
    );

    if (!response.ok) {
      await throwApiError(response, `Failed to delete secret "${secretName}"`);
    }
  }

  async uploadProject(zip: Blob): Promise<UploadProjectResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(`${baseUrl}/uploads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/zip' },
      body: zip,
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to upload project');
    }

    return await response.json();
  }

  async assessUpload(uploadId: string): Promise<UploadAssessment> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(`${baseUrl}/uploads/assess`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId }),
    });

    if (!response.ok && response.status !== 422) {
      await throwApiError(response, 'Failed to check your upload');
    }

    return await response.json();
  }

  async getPendingUpdates(): Promise<PendingUpdate[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(`${baseUrl}/pending-updates`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch pending updates');
    }
    const body = (await response.json()) as { updates?: PendingUpdate[] };
    return body.updates ?? [];
  }

  async abandonPendingUpdate(
    pullRequestNumber: number,
    app: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/pending-updates/${pullRequestNumber}/abandon`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ app }),
      },
    );
    if (!response.ok) {
      await throwApiError(response, 'Failed to abandon pending update');
    }
  }

  async getPublishStatus(name: string): Promise<PublishStatus> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/apps/${encodeURIComponent(name)}/publish-status`,
    );
    if (!response.ok) {
      await throwApiError(
        response,
        `Failed to fetch publish status for "${name}"`,
      );
    }
    return await response.json();
  }

  async downloadSource(name: string): Promise<SourceDownload> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/apps/${encodeURIComponent(name)}/source.zip`,
    );
    if (!response.ok) {
      await throwApiError(response, "Couldn't download the code");
    }
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const fileName =
      /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${name}.zip`;
    return { blob: await response.blob(), fileName };
  }

  async getSpotlight(): Promise<SpotlightDigestResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cloudflare-workers');
    const response = await this.fetchApi.fetch(`${baseUrl}/spotlight`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch Spotlight digest');
    }
    return await response.json();
  }
}
